import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
  RefObject,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/utils'
import ListItem from '@tiptap/extension-list-item'
import { TextStyle, Color } from '@tiptap/extension-text-style'
import TextAlign from '@tiptap/extension-text-align'
import { TaskItem, TaskList } from '@tiptap/extension-list'
import Highlight from '@tiptap/extension-highlight'
import { CharacterCount, Placeholder } from '@tiptap/extensions'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import type {
  EditorEvents,
  Extensions,
  Editor as TipTapEditor,
} from '@tiptap/react'
import type { EditorAttributes } from './lib/types'

import { Marker } from './tiptap/Marker'
import { Icons } from './tiptap/Icons'
import { SlashCommand } from './tiptap/SlashCommand'
import { Submit } from './tiptap/Submit'
import { EditorMenuBar } from './EditorMenuBar'
import { Markup } from './Markup'
import Mention from './tiptap/Mention'

import iconSuggestion from './IconSuggestion'
import mentionSuggestion from './MentionSuggestion'
import { CUSTOM_ICONS, ExtensionsEnum } from './constants'
import { useDocumentState } from '@/state'

const baseExtensions = [
  // @ts-expect-error - types are wrong
  TextStyle.configure({ types: [ListItem.name] }),
  StarterKit.configure({
    bulletList: {
      keepMarks: true,
      keepAttributes: false,
    },
    orderedList: {
      keepMarks: true,
      keepAttributes: false,
    },
    link: {
      openOnClick: true,
      autolink: true,
      linkOnPaste: true,
    },
  }),
]

const EXTENSIONS_MAP = {
  [ExtensionsEnum.ICONS]: Icons.configure({
    emojis: CUSTOM_ICONS,
    suggestion: iconSuggestion,
    HTMLAttributes: {
      class: 'annotation-icon',
    },
  }),
  [ExtensionsEnum.COLOR]: Color.configure({
    types: [TextStyle.name, ListItem.name],
  }),
  [ExtensionsEnum.TEXT_ALIGN]: TextAlign.configure({
    types: ['heading', 'paragraph'],
  }),
  [ExtensionsEnum.MARKER]: Marker.configure({
    multicolor: true,
    HTMLAttributes: {
      class: 'slash-command-pill',
    },
  }),
  [ExtensionsEnum.HIGHLIGHT]: Highlight.configure({
    multicolor: true,
    HTMLAttributes: {
      class: 'highlight',
    },
  }),
  [ExtensionsEnum.TASK_LIST]: TaskList,
  [ExtensionsEnum.TASK_ITEM]: TaskItem.configure({
    nested: true,
  }),
  [ExtensionsEnum.MENTION]: Mention.configure({
    suggestion: mentionSuggestion,
  }),
}

type EditorProps = {
  className?: string
  documentPageId: string
  documentId: string
  projectId: string
  disabled?: boolean
  content: string
  onUpdate: (props: EditorEvents['update']) => void
  onSubmit?: (content?: string) => void
  onBlur?: (props: { editor: TipTapEditor }) => void
  onFocus?: (props: { editor: TipTapEditor }) => void
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void
  onMouseDown?: (event: React.MouseEvent<HTMLDivElement>) => void
  placeholder?: string
  isDragging?: boolean
  menuScale?: number
  menuOffsetY?: number
  updateBomWithSlashCommands?: boolean
  extensionsList?: Array<ExtensionsEnum>
  characterCountLimit?: number
  isTransparent?: boolean
  editorRef?: RefObject<TipTapEditor | null>
}
export const Editor = ({
  className,
  documentId,
  documentPageId,
  projectId,
  disabled,
  content,
  onUpdate,
  onSubmit,
  onBlur,
  onFocus,
  onClick = () => {},
  onMouseDown,
  placeholder,
  isDragging = false,
  menuScale = 1,
  menuOffsetY = 0,
  updateBomWithSlashCommands,
  characterCountLimit,
  isTransparent,
  editorRef,
  extensionsList = [
    ExtensionsEnum.ICONS,
    ExtensionsEnum.LINK,
    ExtensionsEnum.COLOR,
    ExtensionsEnum.UNDERLINE,
    ExtensionsEnum.TEXT_ALIGN,
    ExtensionsEnum.MARKER,
    ExtensionsEnum.HIGHLIGHT,
    ExtensionsEnum.TASK_LIST,
    ExtensionsEnum.TASK_ITEM,
    ExtensionsEnum.MENTION,
    ExtensionsEnum.SLASH_COMMAND,
  ],
}: EditorProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [showMenu, setShowMenu] = useState(false)
  const isAddingElement = useDocumentState((state) => state.isAddingElement)
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  })

  const updateMenuPosition = useCallback(() => {
    if (!ref.current) return
    const rect = ref.current.getBoundingClientRect()
    setMenuPos({
      top: rect.top + window.scrollY - 28 * menuScale,
      left: rect.left + window.scrollX + rect.width / 2,
    })
  }, [menuScale])

  useEffect(() => {
    if (!showMenu || isDragging || disabled) return
    updateMenuPosition()

    const onScroll = () => updateMenuPosition()
    const onResize = () => updateMenuPosition()

    let raf = 0
    const tick = () => {
      updateMenuPosition()
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)

    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onResize)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onResize)
    }
  }, [showMenu, isDragging, updateMenuPosition])

  const extensions = useMemo(() => {
    const e: Extensions = [...baseExtensions]

    extensionsList.forEach((extension) => {
      if (EXTENSIONS_MAP[extension]) {
        e.push(EXTENSIONS_MAP[extension])
      }
    })

    if (extensionsList.includes(ExtensionsEnum.SLASH_COMMAND)) {
      e.push(
        SlashCommand.configure({
          slashComponentProps: {
            updateBomOnSelect: updateBomWithSlashCommands,
          },
        }),
      )
    }
    if (placeholder) {
      e.push(
        Placeholder.configure({
          placeholder,
        }),
      )
    }

    if (onSubmit) {
      e.push(
        Submit.configure({
          onSubmit,
        }),
      )
    }

    if (characterCountLimit) {
      e.push(
        CharacterCount.configure({
          limit: characterCountLimit,
        }),
      )
    }

    return e
  }, [placeholder, extensionsList, characterCountLimit, onSubmit])

  const editorAttributes: EditorAttributes = {
    documentPageId,
    projectId,
    documentId,
    class: cn('tiptap ProseMirror', {
      '!bg-transparent': isTransparent,
    }),
  }

  const editor = useEditor({
    extensions,
    content,
    editable: !isAddingElement,
    onFocus: ({ event }) => {
      if (isAddingElement) {
        const target = event.target as any
        target?.blur()
      }
    },
    editorProps: {
      attributes: editorAttributes,
    },
    onUpdate,
  })

  // Whenever the `content` prop changes, push it into the editor
  useEffect(() => {
    if (!editor) return

    if (editor.getHTML() !== content) {
      editor.commands.setContent(content, {
        emitUpdate: false,
      })
    }
  }, [content, editor])

  useEffect(() => {
    if (editorRef) {
      editorRef.current = editor
    }
  }, [editorRef, editor])

  const handleBlur = useCallback(
    (event) => {
      if (ref.current && ref.current.contains(event.relatedTarget)) {
        return
      }
      setShowMenu(false)

      if (editor && typeof onBlur === 'function') {
        onBlur({ editor })
      }
    },
    [editor, onBlur],
  )

  const handleFocus = useCallback(() => {
    if (!disabled) {
      setShowMenu(true)
    }

    if (editor && typeof onFocus === 'function') {
      onFocus({ editor })
    }
  }, [disabled, editor, onFocus])

  if (!editor) {
    return null
  }

  if (disabled) {
    return (
      <div className="relative h-full w-full" onMouseDown={onMouseDown}>
        <div
          className={cn(
            'cursor-text flex min-h-[45px] h-full w-full rounded-md bg-white px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 flex-1 overflow-auto',
            className,
          )}
        >
          <div className="tiptap ProseMirror !bg-transparent">
            <Markup html={editor.getHTML()} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="relative h-full w-full" ref={ref}>
      <EditorContent
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={onClick}
        onMouseDown={(e) => {
          if (showMenu) {
            e.stopPropagation()
          }
          if (typeof onMouseDown === 'function') {
            onMouseDown(e)
          }
        }}
        className={cn(
          'cursor-text flex min-h-[45px] h-full w-full rounded-md bg-white px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 flex-1 overflow-auto',
          className,
        )}
        editor={editor}
        placeholder={placeholder}
        data-testid="tip-tap-editor"
      >
        {showMenu &&
          !isDragging &&
          createPortal(
            <div
              className="fixed flex justify-center"
              style={{
                top: `${menuPos.top + menuOffsetY}px`,
                left: `${menuPos.left}px`,
                transform: `translateX(-50%) scale(${menuScale})`,
                zIndex: 999999,
                pointerEvents: 'auto',
              }}
            >
              <EditorMenuBar editor={editor} extensionsList={extensionsList} />
            </div>,
            document.body,
          )}
      </EditorContent>
    </div>
  )
}
